import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Shield, AlertTriangle, Plus, ArrowLeft, Printer, ChevronRight, FileWarning,
  CheckCircle2, Scale, Users2, MessageSquare, Gavel, ClipboardList, Filter, Lock,
} from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_META = {
  low: { label: 'Low', color: '#16A34A', bg: '#16A34A15' },
  medium: { label: 'Medium', color: '#D97706', bg: '#D9770615' },
  high: { label: 'High', color: '#DC2626', bg: '#DC262615' },
};
const STATUS_META = {
  open: { label: 'Open', color: '#D97706', bg: '#D9770615' },
  in_mediation: { label: 'In mediation', color: '#2563EB', bg: '#2563EB15' },
  resolved: { label: 'Resolved', color: '#16A34A', bg: '#16A34A15' },
  escalated: { label: 'Escalated', color: '#DC2626', bg: '#DC262615' },
};

const PURPLE = '#7C3AED';

function Pill({ meta, testid }) {
  if (!meta) return null;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full"
      style={{ backgroundColor: meta.bg, color: meta.color }}>
      {meta.label}
    </span>
  );
}

// ---------- LIST PAGE ----------

export function DisputesListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', severity: '' });

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filters.status) p.set('status', filters.status);
    if (filters.severity) p.set('severity', filters.severity);
    const r = await fetch(`${API}/disputes?${p.toString()}`, { credentials: 'include' });
    setRows(r.ok ? await r.json() : []);
    if (isPM) {
      const s = await fetch(`${API}/disputes/stats`, { credentials: 'include' });
      if (s.ok) setStats(await s.json());
    }
    setLoading(false);
  }, [filters, isPM]);

  useEffect(() => { load(); }, [load]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="disputes-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/management" data-testid="disp-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5">
          <ArrowLeft className="w-4 h-4" /> Back to Management
        </Link>

        <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
          <div className="rounded-2xl border p-5 flex-1 min-w-[280px]" style={{ borderColor: `${PURPLE}33`, background: `linear-gradient(135deg, ${PURPLE}0A, transparent 60%)` }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${PURPLE}15`, color: PURPLE }}>
                <Scale className="w-6 h-6" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${PURPLE}1A`, color: PURPLE }}>HR · Conflict Register</span>
            </div>
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Staff Disputes &amp; Mediation</h1>
            <p className="text-sm text-muted-foreground mt-1">Structured dispute reporting · mediation template · audit trail for governance.</p>
          </div>
          <button onClick={() => navigate('/disputes/new')} data-testid="disp-new-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-xl text-white shrink-0"
            style={{ backgroundColor: PURPLE }}>
            <Plus className="w-4 h-4" /> Report dispute
          </button>
        </div>

        {/* Escalation rule notice */}
        <div className="rounded-xl border p-4 mb-6 flex items-start gap-3"
          style={{ borderColor: '#DC262633', backgroundColor: '#DC26260A' }}>
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#DC2626' }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: '#DC2626' }}>Any dispute that risks patient care must be escalated immediately to the Medical Director.</p>
            <p className="text-xs text-muted-foreground mt-0.5">High-severity reports auto-email all admin users for immediate review.</p>
          </div>
        </div>

        {isPM && stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="disp-stats">
            <Kpi label="Total" value={stats.total} color={PURPLE} />
            <Kpi label="Open" value={stats.open} color="#D97706" />
            <Kpi label="In mediation" value={stats.in_mediation} color="#2563EB" />
            <Kpi label="High severity" value={stats.high} color={stats.high ? '#DC2626' : '#16A34A'} />
          </div>
        )}

        {/* Filters */}
        <div className="rounded-xl border border-border bg-card p-4 mb-4 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground"><Filter className="w-3.5 h-3.5" />Filters</div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Status</label>
            <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} data-testid="disp-filter-status" className="text-sm bg-background border border-border rounded-lg px-2 py-1.5">
              <option value="">All</option>
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Severity</label>
            <select value={filters.severity} onChange={e => setFilters({ ...filters, severity: e.target.value })} data-testid="disp-filter-severity" className="text-sm bg-background border border-border rounded-lg px-2 py-1.5">
              <option value="">All</option>
              {Object.entries(SEV_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>

        {/* Register */}
        <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="disp-register">
          {loading && <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">No disputes on record. {isPM ? '' : 'Only disputes where you are a party are visible to you.'}</div>
          )}
          {!loading && rows.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-3 py-2">Case ID</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Parties</th>
                  <th className="px-3 py-2">Nature</th>
                  <th className="px-3 py-2">Severity</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d, i) => (
                  <tr key={d.dispute_id} className="border-b border-border/60 last:border-0 hover:bg-muted/30" data-testid={`disp-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-xs text-foreground">{d.dispute_id.slice(0, 8)}</td>
                    <td className="px-3 py-2 text-foreground whitespace-nowrap">{(d.created_at || '').slice(0, 10)}</td>
                    <td className="px-3 py-2 text-foreground text-xs">{d.parties?.party_1} · {d.parties?.party_2}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground max-w-[200px] truncate">{(d.nature || []).join(', ')}</td>
                    <td className="px-3 py-2"><Pill meta={SEV_META[d.severity]} /></td>
                    <td className="px-3 py-2"><Pill meta={STATUS_META[d.status]} /></td>
                    <td className="px-3 py-2">
                      <button onClick={() => navigate(`/disputes/${d.dispute_id}`)} data-testid={`disp-open-${i}`}
                        className="inline-flex items-center gap-0.5 text-xs font-semibold" style={{ color: PURPLE }}>
                        Open <ChevronRight className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
      <p className="font-['Outfit'] text-2xl font-black" style={{ color }}>{value}</p>
    </div>
  );
}

// ---------- NEW DISPUTE FORM ----------

const INITIAL_FORM = {
  party_1: '', party_2: '', others: '',
  nature_communication: false, nature_interpersonal: false, nature_role: false, nature_behaviour: false, nature_other: '',
  description: '', incident_dates: '',
  impact_level: 'none', impact_details: '',
  informal_attempted: false, informal_detail: '',
  desired_outcome: '',
  submitted_to: 'pm',
};

export function DisputeFormPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      const r = await fetch(`${API}/disputes`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(f),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || 'Failed to submit');
      }
      const d = await r.json();
      navigate(`/disputes/${d.dispute_id}?created=1`);
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  const onPrint = () => window.print();

  return (
    <div className="min-h-screen bg-background" data-testid="disp-form-page">
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } }`}</style>
      <div className="no-print"><Header /></div>
      <form onSubmit={submit} className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6 no-print">
          <Link to="/disputes" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Conflict Register
          </Link>
          <button type="button" onClick={onPrint} data-testid="disp-form-print"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border"
            style={{ borderColor: `${PURPLE}40`, color: PURPLE }}>
            <Printer className="w-3.5 h-3.5" /> Print / PDF
          </button>
        </div>

        <div className="rounded-2xl border p-6 mb-5 bg-card" style={{ borderColor: `${PURPLE}33` }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${PURPLE}15`, color: PURPLE }}>
              <FileWarning className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${PURPLE}1A`, color: PURPLE }}>SP Radiology · HR Form</span>
          </div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-foreground tracking-tight">Staff Dispute Reporting Form</h1>
          <p className="text-sm text-muted-foreground mt-1">Confidential — only the Practice Manager and Admin see the reporter's identity.</p>
          <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
            <div><span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block mb-0.5">Date of report</span><span className="text-foreground">{new Date().toISOString().slice(0, 10)}</span></div>
            <div><span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block mb-0.5">Reported by</span><span className="text-foreground">{user.name} <span className="text-muted-foreground text-xs">({user.role})</span></span></div>
          </div>
        </div>

        {err && <div className="mb-4 p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-600" data-testid="disp-form-error">{err}</div>}

        <Section n={1} title="Parties involved">
          <Field label="Staff Member 1">
            <input required value={f.party_1} onChange={e => setF({ ...f, party_1: e.target.value })} data-testid="disp-party1" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </Field>
          <Field label="Staff Member 2">
            <input required value={f.party_2} onChange={e => setF({ ...f, party_2: e.target.value })} data-testid="disp-party2" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </Field>
          <Field label="Others (if any)">
            <input value={f.others} onChange={e => setF({ ...f, others: e.target.value })} data-testid="disp-others" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </Field>
        </Section>

        <Section n={2} title="Nature of dispute">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Checkbox checked={f.nature_communication} onChange={v => setF({ ...f, nature_communication: v })} label="Communication breakdown" testid="disp-n-comm" />
            <Checkbox checked={f.nature_interpersonal} onChange={v => setF({ ...f, nature_interpersonal: v })} label="Interpersonal conflict" testid="disp-n-inter" />
            <Checkbox checked={f.nature_role} onChange={v => setF({ ...f, nature_role: v })} label="Role/responsibility issue" testid="disp-n-role" />
            <Checkbox checked={f.nature_behaviour} onChange={v => setF({ ...f, nature_behaviour: v })} label="Behaviour concern" testid="disp-n-beh" />
          </div>
          <Field label="Other (describe)">
            <input value={f.nature_other} onChange={e => setF({ ...f, nature_other: e.target.value })} data-testid="disp-n-other" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </Field>
        </Section>

        <Section n={3} title="Description of incident">
          <Field label="Provide a factual, objective description">
            <textarea required rows={5} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} data-testid="disp-description" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </Field>
        </Section>

        <Section n={4} title="Date(s) and time(s) of incident">
          <Field label="When">
            <input required value={f.incident_dates} onChange={e => setF({ ...f, incident_dates: e.target.value })} placeholder="e.g. 12 Apr 2026, ~14:30 in reception" data-testid="disp-when" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </Field>
        </Section>

        <Section n={5} title="Impact on work / patient care">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { v: 'none', l: 'No impact' },
              { v: 'minor', l: 'Minor disruption' },
              { v: 'moderate', l: 'Moderate disruption' },
              { v: 'significant', l: 'Significant impact on patient care' },
            ].map(o => (
              <label key={o.v} className="flex items-center gap-2 text-sm text-foreground cursor-pointer rounded-lg border border-border px-3 py-2 hover:bg-muted/30">
                <input type="radio" name="impact" checked={f.impact_level === o.v} onChange={() => setF({ ...f, impact_level: o.v })} data-testid={`disp-impact-${o.v}`} />
                <span>{o.l}</span>
                {o.v === 'significant' && <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#DC262615', color: '#DC2626' }}>Auto-escalates</span>}
              </label>
            ))}
          </div>
          <Field label="Details">
            <textarea rows={3} value={f.impact_details} onChange={e => setF({ ...f, impact_details: e.target.value })} data-testid="disp-impact-details" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </Field>
        </Section>

        <Section n={6} title="Has informal resolution been attempted?">
          <Checkbox checked={f.informal_attempted} onChange={v => setF({ ...f, informal_attempted: v })} label="Yes" testid="disp-informal-yes" />
          {f.informal_attempted && (
            <Field label="Describe what was tried">
              <textarea rows={2} value={f.informal_detail} onChange={e => setF({ ...f, informal_detail: e.target.value })} data-testid="disp-informal-detail" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
            </Field>
          )}
        </Section>

        <Section n={7} title="Desired outcome (if known)">
          <Field label="">
            <textarea rows={3} value={f.desired_outcome} onChange={e => setF({ ...f, desired_outcome: e.target.value })} data-testid="disp-outcome" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </Field>
        </Section>

        <Section n={8} title="Submitted to">
          <div className="flex flex-wrap gap-2">
            {[
              { v: 'pm', l: 'Practice Manager' },
              { v: 'md', l: 'Medical Director' },
              { v: 'both', l: 'Both' },
            ].map(o => (
              <label key={o.v} className="flex items-center gap-2 text-sm text-foreground cursor-pointer rounded-lg border border-border px-3 py-2 hover:bg-muted/30">
                <input type="radio" name="submitted_to" checked={f.submitted_to === o.v} onChange={() => setF({ ...f, submitted_to: o.v })} data-testid={`disp-to-${o.v}`} />
                <span>{o.l}</span>
              </label>
            ))}
          </div>
        </Section>

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-border no-print">
          <p className="text-xs text-muted-foreground max-w-sm">By submitting, you acknowledge this report becomes part of SP Radiology's governance record and will be handled confidentially by the Practice Manager or Medical Director.</p>
          <button type="submit" disabled={saving} data-testid="disp-form-submit"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-xl text-white disabled:opacity-60"
            style={{ backgroundColor: PURPLE }}>
            <FileWarning className="w-4 h-4" /> {saving ? 'Submitting…' : 'Submit dispute'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 mb-4" data-testid={`disp-section-${n}`}>
      <h2 className="font-['Outfit'] text-base font-semibold text-foreground mb-3 flex items-center gap-2">
        <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center" style={{ backgroundColor: `${PURPLE}15`, color: PURPLE }}>{n}</span>
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      {label && <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>}
      <div className={label ? 'mt-1' : ''}>{children}</div>
    </label>
  );
}

function Checkbox({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} data-testid={testid} />
      <span>{label}</span>
    </label>
  );
}

// ---------- DETAIL + MEDIATION ----------

export function DisputeDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showMed, setShowMed] = useState(false);
  const [showClose, setShowClose] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/disputes/${id}`, { credentials: 'include' });
    if (!r.ok) {
      setErr((await r.json()).detail || 'Not found');
      setDoc(null);
    } else {
      setDoc(await r.json());
      setErr('');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!user) return null;
  if (loading) return <div className="min-h-screen bg-background"><Header /><div className="p-10 text-center text-sm text-muted-foreground">Loading…</div></div>;
  if (err || !doc) return (
    <div className="min-h-screen bg-background"><Header />
      <div className="max-w-xl mx-auto p-10 text-center">
        <Lock className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm text-foreground font-semibold">{err || 'Dispute not found'}</p>
        <Link to="/disputes" className="text-xs" style={{ color: PURPLE }}>Back to Conflict Register</Link>
      </div>
    </div>
  );

  const isClosed = doc.status === 'resolved' || doc.status === 'escalated';

  const onPrint = () => window.print();

  return (
    <div className="min-h-screen bg-background" data-testid="disp-detail-page">
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } }`}</style>
      <div className="no-print"><Header /></div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-5 no-print">
          <Link to="/disputes" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Conflict Register
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={onPrint} data-testid="disp-detail-print" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border" style={{ borderColor: `${PURPLE}40`, color: PURPLE }}>
              <Printer className="w-3.5 h-3.5" /> Print / PDF
            </button>
            {isPM && !isClosed && (
              <>
                <button onClick={() => setShowMed(true)} data-testid="disp-add-mediation" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white" style={{ backgroundColor: '#2563EB' }}>
                  <Gavel className="w-3.5 h-3.5" /> Add mediation session
                </button>
                <button onClick={() => setShowClose(true)} data-testid="disp-close-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white" style={{ backgroundColor: '#16A34A' }}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Close
                </button>
              </>
            )}
          </div>
        </div>

        {/* Header card */}
        <div className="rounded-2xl border p-5 mb-4 bg-card" style={{ borderColor: `${PURPLE}33` }}>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Pill meta={SEV_META[doc.severity]} testid="disp-sev" />
            <Pill meta={STATUS_META[doc.status]} testid="disp-status" />
            <span className="text-[11px] font-mono text-muted-foreground">#{doc.dispute_id}</span>
            <span className="text-[11px] text-muted-foreground ml-auto">{(doc.created_at || '').slice(0, 10)}</span>
          </div>
          <h1 className="font-['Outfit'] text-xl sm:text-2xl font-bold text-foreground">Dispute — {doc.parties?.party_1} vs {doc.parties?.party_2}</h1>
          <p className="text-xs text-muted-foreground mt-1">Reported to: {doc.submitted_to === 'pm' ? 'Practice Manager' : doc.submitted_to === 'md' ? 'Medical Director' : 'PM + Medical Director'}</p>
          {isPM ? (
            <p className="text-xs text-foreground mt-2"><span className="uppercase tracking-wider text-muted-foreground text-[10px] mr-2">Reporter</span>{doc.reported_by_name} · {doc.reported_by_email}</p>
          ) : (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Lock className="w-3 h-3" /> Reporter identity is confidential to PM/Admin.</p>
          )}
        </div>

        {/* Form content summary */}
        <div className="rounded-2xl border border-border bg-card p-5 mb-4">
          <h3 className="font-['Outfit'] text-base font-semibold text-foreground mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4" style={{ color: PURPLE }} /> Report details</h3>
          <Row label="Parties" value={`${doc.parties?.party_1} · ${doc.parties?.party_2}${doc.parties?.others ? ' · Others: ' + doc.parties.others : ''}`} />
          <Row label="Nature" value={(doc.nature || []).join(', ') || '—'} />
          <Row label="Description" value={doc.description} />
          <Row label="Incident date(s)" value={doc.incident_dates} />
          <Row label="Impact level" value={doc.impact_level} />
          {doc.impact_details && <Row label="Impact details" value={doc.impact_details} />}
          <Row label="Informal resolution attempted" value={doc.informal_attempted ? `Yes — ${doc.informal_detail || ''}` : 'No'} />
          {doc.desired_outcome && <Row label="Desired outcome" value={doc.desired_outcome} />}
        </div>

        {/* Mediation sessions */}
        <div className="rounded-2xl border border-border bg-card p-5 mb-4">
          <h3 className="font-['Outfit'] text-base font-semibold text-foreground mb-3 flex items-center gap-2"><Users2 className="w-4 h-4" style={{ color: '#2563EB' }} /> Mediation sessions ({(doc.mediation_sessions || []).length})</h3>
          {(!doc.mediation_sessions || doc.mediation_sessions.length === 0) && (
            <p className="text-sm text-muted-foreground">No mediation sessions yet. {isPM && !isClosed && 'Use "Add mediation session" above to record one.'}</p>
          )}
          {(doc.mediation_sessions || []).map((s, i) => (
            <MediationCard key={s.session_id} s={s} i={i} />
          ))}
        </div>

        {/* Closure */}
        {isClosed && (
          <div className="rounded-2xl border p-5 mb-4" style={{ borderColor: doc.closure_outcome === 'resolved' ? '#16A34A40' : '#DC262640', backgroundColor: doc.closure_outcome === 'resolved' ? '#16A34A0A' : '#DC26260A' }}>
            <h3 className="font-['Outfit'] text-base font-semibold text-foreground mb-2 flex items-center gap-2">
              {doc.closure_outcome === 'resolved' ? <CheckCircle2 className="w-4 h-4" style={{ color: '#16A34A' }} /> : <AlertTriangle className="w-4 h-4" style={{ color: '#DC2626' }} />}
              Closed — {doc.closure_outcome}
            </h3>
            <Row label="Closed at" value={(doc.closed_at || '').slice(0, 10)} />
            <Row label="Closed by" value={doc.closed_by_email} />
            {doc.closure_note && <Row label="Closure note" value={doc.closure_note} />}
          </div>
        )}

        {/* Audit trail - PM only */}
        {isPM && (
          <details className="rounded-xl border border-border bg-card p-4 no-print">
            <summary className="text-xs font-bold uppercase tracking-wider text-muted-foreground cursor-pointer">Audit trail ({(doc.audit_trail || []).length})</summary>
            <ul className="mt-2 space-y-1 text-xs text-foreground">
              {(doc.audit_trail || []).map((a, i) => (
                <li key={i}><span className="font-mono text-muted-foreground">{(a.at || '').slice(0, 16)}</span> — {a.action} by {a.by} {a.severity ? `(severity=${a.severity})` : ''}{a.status ? `(status=${a.status})` : ''}</li>
              ))}
            </ul>
          </details>
        )}
      </div>

      {showMed && <MediationModal did={doc.dispute_id} onClose={() => setShowMed(false)} onSaved={() => { setShowMed(false); load(); }} />}
      {showClose && <CloseModal did={doc.dispute_id} onClose={() => setShowClose(false)} onSaved={() => { setShowClose(false); load(); }} />}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="py-2 border-b border-border/50 last:border-0 grid grid-cols-[140px_1fr] gap-3">
      <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground whitespace-pre-wrap">{value || '—'}</span>
    </div>
  );
}

function MediationCard({ s, i }) {
  return (
    <div className="rounded-xl border border-border p-4 mb-2" data-testid={`disp-mediation-${i}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: '#2563EB15', color: '#2563EB' }}>Session {i + 1}</span>
        <span className="text-xs text-muted-foreground">{s.session_date}</span>
        <span className="text-xs text-muted-foreground ml-auto">Facilitator: {s.facilitator_name} ({s.facilitator_role === 'pm' ? 'PM' : 'MD'})</span>
      </div>
      <Row label="Person A" value={`${s.person_a_name} — ${s.person_a_statement}`} />
      <Row label="Person B" value={`${s.person_b_name} — ${s.person_b_statement}`} />
      <Row label="Key issues" value={s.key_issues} />
      <Row label="Resolution discussion" value={s.resolution_discussion} />
      {(s.agreed_actions || []).length > 0 && (
        <div className="py-2 border-b border-border/50">
          <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">Agreed action plan</span>
          <table className="w-full text-xs border border-border/50 rounded">
            <thead><tr className="bg-muted/30"><th className="p-1.5 text-left font-semibold">Action</th><th className="p-1.5 text-left font-semibold">Responsible</th><th className="p-1.5 text-left font-semibold">Timeline</th></tr></thead>
            <tbody>{s.agreed_actions.map((a, j) => <tr key={j} className="border-t border-border/40"><td className="p-1.5">{a.action}</td><td className="p-1.5">{a.responsible}</td><td className="p-1.5">{a.timeline}</td></tr>)}</tbody>
          </table>
        </div>
      )}
      {s.closing_notes && <Row label="Closing notes" value={s.closing_notes} />}
      {s.next_review_date && <Row label="Next review" value={s.next_review_date} />}
    </div>
  );
}

function MediationModal({ did, onClose, onSaved }) {
  const [f, setF] = useState({
    facilitator_role: 'pm', session_date: new Date().toISOString().slice(0, 10),
    person_a_name: '', person_a_statement: '',
    person_b_name: '', person_b_statement: '',
    key_issues: '', resolution_discussion: '',
    agreed_actions: [{ action: '', responsible: '', timeline: '' }],
    closing_notes: '', next_review_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const addRow = () => setF({ ...f, agreed_actions: [...f.agreed_actions, { action: '', responsible: '', timeline: '' }] });
  const updateRow = (i, k, v) => {
    const a = [...f.agreed_actions]; a[i] = { ...a[i], [k]: v };
    setF({ ...f, agreed_actions: a });
  };
  const removeRow = (i) => setF({ ...f, agreed_actions: f.agreed_actions.filter((_, j) => j !== i) });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const body = { ...f, agreed_actions: f.agreed_actions.filter(a => a.action.trim()) };
      const r = await fetch(`${API}/disputes/${did}/mediation`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
        className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="disp-med-modal">
        <div className="p-5 border-b border-border">
          <h3 className="font-['Outfit'] text-lg font-bold text-foreground flex items-center gap-2"><Gavel className="w-5 h-5" style={{ color: '#2563EB' }} /> Mediation Session Template</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Structured 7-step mediation record. Adding a session moves the dispute to "In mediation".</p>
        </div>
        <div className="p-5 space-y-3">
          {err && <div className="p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-600">{err}</div>}
          <MediationSectionHeader step={1} title="Opening" subtitle="This is a confidential discussion aimed at resolving the issue constructively." />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Facilitator role">
              <select value={f.facilitator_role} onChange={e => setF({ ...f, facilitator_role: e.target.value })} data-testid="disp-med-role" className="w-full text-sm bg-background border border-border rounded-lg px-2 py-1.5">
                <option value="pm">Practice Manager</option>
                <option value="md">Medical Director</option>
              </select>
            </Field>
            <Field label="Session date">
              <input required type="date" value={f.session_date} onChange={e => setF({ ...f, session_date: e.target.value })} data-testid="disp-med-date" className="w-full text-sm bg-background border border-border rounded-lg px-2 py-1.5" />
            </Field>
          </div>

          <MediationSectionHeader step={2} title="Ground rules" subtitle="Respect · no interruptions · facts &amp; solutions · no personal attacks." />

          <MediationSectionHeader step={3} title="Individual statements" subtitle="What happened? How did it affect you? What outcome do you want?" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Person A name"><input required value={f.person_a_name} onChange={e => setF({ ...f, person_a_name: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-2 py-1.5" data-testid="disp-med-a-name" /></Field>
            <Field label="Person B name"><input required value={f.person_b_name} onChange={e => setF({ ...f, person_b_name: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-2 py-1.5" data-testid="disp-med-b-name" /></Field>
            <Field label="Person A statement"><textarea required rows={3} value={f.person_a_statement} onChange={e => setF({ ...f, person_a_statement: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-2 py-1.5" data-testid="disp-med-a-stmt" /></Field>
            <Field label="Person B statement"><textarea required rows={3} value={f.person_b_statement} onChange={e => setF({ ...f, person_b_statement: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-2 py-1.5" data-testid="disp-med-b-stmt" /></Field>
          </div>

          <MediationSectionHeader step={4} title="Identify key issues" subtitle="Facilitator summary." />
          <Field label=""><textarea required rows={2} value={f.key_issues} onChange={e => setF({ ...f, key_issues: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-2 py-1.5" data-testid="disp-med-issues" /></Field>

          <MediationSectionHeader step={5} title="Resolution discussion" subtitle="What changes? What support is needed?" />
          <Field label=""><textarea required rows={2} value={f.resolution_discussion} onChange={e => setF({ ...f, resolution_discussion: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-2 py-1.5" data-testid="disp-med-resolution" /></Field>

          <MediationSectionHeader step={6} title="Agreement — Action plan" subtitle="Document agreed actions, owners and timelines." />
          <table className="w-full text-xs border border-border rounded">
            <thead><tr className="bg-muted/30"><th className="p-1.5 text-left">Action</th><th className="p-1.5 text-left">Responsible</th><th className="p-1.5 text-left">Timeline</th><th></th></tr></thead>
            <tbody>
              {f.agreed_actions.map((a, i) => (
                <tr key={i} className="border-t border-border/40">
                  <td className="p-1"><input value={a.action} onChange={e => updateRow(i, 'action', e.target.value)} data-testid={`disp-med-action-${i}`} className="w-full text-xs bg-background border border-border rounded px-2 py-1" /></td>
                  <td className="p-1"><input value={a.responsible} onChange={e => updateRow(i, 'responsible', e.target.value)} className="w-full text-xs bg-background border border-border rounded px-2 py-1" /></td>
                  <td className="p-1"><input value={a.timeline} onChange={e => updateRow(i, 'timeline', e.target.value)} className="w-full text-xs bg-background border border-border rounded px-2 py-1" /></td>
                  <td className="p-1 text-center"><button type="button" onClick={() => removeRow(i)} className="text-muted-foreground hover:text-red-600 text-xs">✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" onClick={addRow} data-testid="disp-med-addrow" className="text-xs font-semibold" style={{ color: PURPLE }}>+ Add action</button>

          <MediationSectionHeader step={7} title="Closing" subtitle="Review progress in 2–4 weeks. Expectation: professional and respectful working relationship." />
          <Field label="Closing notes"><textarea rows={2} value={f.closing_notes} onChange={e => setF({ ...f, closing_notes: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-2 py-1.5" /></Field>
          <Field label="Next review date"><input type="date" value={f.next_review_date} onChange={e => setF({ ...f, next_review_date: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-2 py-1.5" /></Field>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-border">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-border text-foreground">Cancel</button>
          <button type="submit" disabled={saving} data-testid="disp-med-submit" className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-60" style={{ backgroundColor: '#2563EB' }}>
            <Gavel className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save session'}
          </button>
        </div>
      </form>
    </div>
  );
}

function MediationSectionHeader({ step, title, subtitle }) {
  return (
    <div className="border-l-4 pl-3 py-1" style={{ borderColor: `${PURPLE}66` }}>
      <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: PURPLE }}>Step {step} — {title}</p>
      <p className="text-[11px] text-muted-foreground">{subtitle}</p>
    </div>
  );
}

function CloseModal({ did, onClose, onSaved }) {
  const [outcome, setOutcome] = useState('resolved');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    const r = await fetch(`${API}/disputes/${did}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: outcome, closure_note: note }),
    });
    if (r.ok) onSaved(); else { alert((await r.json()).detail || 'Failed'); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-card border border-border rounded-2xl max-w-md w-full p-5" data-testid="disp-close-modal">
        <h3 className="font-['Outfit'] text-lg font-bold text-foreground mb-2">Close dispute</h3>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Outcome</label>
            <div className="flex gap-2 mt-1">
              {['resolved', 'escalated'].map(o => (
                <label key={o} className="flex items-center gap-2 text-sm cursor-pointer border border-border rounded-lg px-3 py-2 flex-1">
                  <input type="radio" checked={outcome === o} onChange={() => setOutcome(o)} data-testid={`disp-close-${o}`} />
                  <span className="capitalize">{o}</span>
                </label>
              ))}
            </div>
          </div>
          <Field label="Closure note">
            <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} data-testid="disp-close-note" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-border">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
          <button type="submit" disabled={saving} data-testid="disp-close-submit" className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white" style={{ backgroundColor: '#16A34A' }}>{saving ? 'Saving…' : 'Close'}</button>
        </div>
      </form>
    </div>
  );
}
